import { create } from "zustand";
import type { TagRequest, Tag, TagState, TagUpdateRequest, TagPaginatedResponse, MinimalTag } from "../types";
import { tagApi } from "../services/tag-api";

export const useTagStore = create<TagState>((set) => ({
	tags: null,
	index: async (workspaceId: string, cursor?: string | null): Promise<TagPaginatedResponse | null> => {
		const result = await tagApi.index(workspaceId, cursor);

		if (result.success && result.data) {
			const data = result.data;
			if (cursor) {
				// Append to existing tags when loading more, avoiding duplicates
				set((state) => {
					if (!state.tags) return { tags: data.data };
					const existingIds = new Set(state.tags.map((t) => t.id));
					const newTags = data.data.filter((t) => !existingIds.has(t.id));
					return { tags: [...state.tags, ...newTags] };
				});
			} else {
				// Replace tags on initial load
				set({ tags: data.data });
			}
			return data;
		}
		return null;
	},

	search: async (workspaceId: string, query: string, cursor?: string | null): Promise<TagPaginatedResponse | null> => {
		const result = await tagApi.index(workspaceId, cursor, query);

		if (result.success && result.data) {
			return result.data;
		}
		return null;
	},

	create: async (workspaceId: string, payload: TagRequest) => {
		const response = await tagApi.create(workspaceId, payload);
		if (response.data !== null) {
			const newTag: Tag = response.data!;
			set((state) => ({
				tags: state.tags !== null ? [...state.tags, newTag] : [newTag],
			}));
		}
		return response;
	},

	update: async (id: string, workspaceId: string, payload: TagUpdateRequest) => {
		const response = await tagApi.update(id, workspaceId, payload);
		set((state) => ({
			tags: state.tags !== null ? state.tags.map((tag: Tag) => (tag.id === id ? (response.data ?? tag) : tag)) : null,
		}));
		return response;
	},

	delete: async (id: string, workspaceId: string) => {
		const response = await tagApi.delete(id, workspaceId);
		set((state) => ({ tags: state.tags !== null ? state.tags.filter((tag: Tag) => tag.id !== id) : null }));
		return response;
	},

	getByIds: async (workspaceId: string, ids: string[]): Promise<Tag[] | null> => {
		if (ids.length === 0) return [];
		const response = await tagApi.getByIds(workspaceId, ids);
		if (response.success && response.data) {
			return response.data;
		}
		return null;
	},

	mergeSelectedTags: (selectedTags: MinimalTag[]) => {
		set((state) => {
			// Cast minimal tags to Tag for storage (they'll only be used for display with id, label, color)
			const tagsToMerge = selectedTags as unknown as Tag[];
			if (!state.tags) return { tags: tagsToMerge };
			// Merge selected tags with existing tags, avoiding duplicates
			const existingIds = new Set(state.tags.map((t) => t.id));
			const newTags = tagsToMerge.filter((t) => !existingIds.has(t.id));
			return { tags: [...newTags, ...state.tags] };
		});
	},
}));
